import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Cross, GreenTick, Location, Success } from '../../../assests/ExportImageIcon'
import { VENUE_PRICING_TYPE_LABELS } from '../../../utils/venueFormat'
import Modal from '../../../componants/modal/Modal'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import '../venueModals.css'
import '../venueShared.css'
import './venueProposalDetail.css'

const TABS = ['Pricing', 'Inclusions', 'Terms']

const PAYMENT_TERM_LABELS = {
    advance_payment: 'Advance Payment',
    payment_before_event: 'Payment Before Event',
    final_payment: 'Final Payment',
}

const formatPrice = (value, currency) => {
    const amount = Number(value)
    if (Number.isNaN(amount)) return null
    const symbol = !currency || currency === 'INR' ? '₹' : `${currency} `
    return `${symbol}${amount.toLocaleString('en-IN')}`
}

const formatDate = (isoStr) => {
    if (!isoStr) return ''
    const date = new Date(isoStr)
    if (Number.isNaN(date.getTime())) return isoStr
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
}

// farebreakup: array of {id, name, amount, currency, ...}
const fareBreakupRowsFrom = (raw) =>
    Array.isArray(raw)
        ? raw.map((item) => ({ id: item.id, label: item.name || 'Item', value: formatPrice(item.amount, item.currency) || '—' }))
        : []

// inclusion: array of {rule_type: 'included' | 'not_included', description}
const inclusionListsFrom = (raw) => {
    const list = Array.isArray(raw) ? raw : []
    return {
        included: list.filter((item) => item.rule_type === 'included').map((item) => item.description).filter(Boolean),
        notIncluded: list.filter((item) => item.rule_type !== 'included').map((item) => item.description).filter(Boolean),
    }
}

// terms: { payment_terms: {advance_payment, payment_before_event, final_payment} (percentages),
//          cancellation_policies: [{policy_type, description, duration_before, refund_percentage}] }
const paymentTermRowsFrom = (terms) => {
    const paymentTerms = terms?.payment_terms
    if (!paymentTerms || typeof paymentTerms !== 'object') return []
    return Object.entries(paymentTerms).map(([key, value]) => ({
        key,
        label: PAYMENT_TERM_LABELS[key] || key,
        value: `${value}%`,
    }))
}

const cancellationRowsFrom = (terms) => {
    const policies = Array.isArray(terms?.cancellation_policies) ? terms.cancellation_policies : []
    return policies.map((policy, index) => {
        const unit = policy.policy_type + (policy.duration_before === 1 || String(policy.policy_type).endsWith('s') ? '' : 's')
        const parts = [`${policy.duration_before} ${unit} before event`]
        if (policy.refund_percentage != null) parts.push(`${policy.refund_percentage}% refund`)
        if (policy.description) parts.push(policy.description)
        return { key: index, text: parts.join(' — ') }
    })
}

const VenueProposalDetail = () => {
    const navigate = useNavigate()
    const { id } = useParams()
    const [quotation, setQuotation] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [activeTab, setActiveTab] = useState('Pricing')
    const [rejectOpen, setRejectOpen] = useState(false)
    const [acceptOpen, setAcceptOpen] = useState(false)
    const [actionLoading, setActionLoading] = useState(false)

    useEffect(() => {
        if (!id) return
        setLoading(true)
        setLoadError(false)
        service
            .get(endpoints.venues.proposalDetail.replace('{proposal_id}', id), {}, { silent: true })
            .then(({ data }) => setQuotation(data?.data ?? data ?? null))
            .catch(() => {
                setQuotation(null)
                setLoadError(true)
            })
            .finally(() => setLoading(false))
    }, [id])

    if (loading) {
        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p className='hotelResultsEmpty'>Loading proposal...</p>
                </div>
            </section>
        )
    }

    if (loadError || !quotation) {
        return (
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p className='hotelResultsEmpty'>This proposal couldn&apos;t be found. It may have been removed or the link is incorrect.</p>
                    <button type='button' className='commonBtn' onClick={() => navigate('/venue-booking-confirm')}>Back to Proposals</button>
                </div>
            </section>
        )
    }

    // start_date/end_date replaced the old preferred_date field on this
    // endpoint (matching the same RFP schema change on the submit side) — a
    // single-day booking has start_date === end_date, shown as one date.
    const proposalDateLabel = quotation.start_date
        ? (quotation.end_date && quotation.end_date !== quotation.start_date
            ? `${formatDate(quotation.start_date)} – ${formatDate(quotation.end_date)}`
            : formatDate(quotation.start_date))
        : ''
    const proposal = {
        venueName: quotation.venue_name || '',
        location: quotation.venue_location || '',
        area: quotation.venue_area || '',
        guests: quotation.guest_count ?? '—',
        eventType: quotation.event_type_name || '',
        preferredDate: proposalDateLabel,
        timeSlot: quotation.time_slot || '',
        budgetRange: quotation.budget_range || '',
        status: String(quotation.status || 'pending').toLowerCase(),
        statusLabel: quotation.status_label || '',
        submitted: quotation.submitted || '',
        additionalRequirements: quotation.additional_requirements || '',
        requestId: quotation.request_id || '',
        pricingType: quotation.pricing_type
            ? (VENUE_PRICING_TYPE_LABELS[quotation.pricing_type] || quotation.pricing_type)
            : '',
        estimatedPrice: formatPrice(quotation.total_estimated_price, quotation.currency),
        negotiationStatus: quotation.negotiation_status
            ? quotation.negotiation_status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
            : '',
    }
    const badgeClass = proposal.status === 'accepted' ? 'is-confirmed'
        : (proposal.status === 'rejected' || proposal.status === 'declined') ? 'is-cancelled'
            : 'is-pending'

    const fareBreakupRows = fareBreakupRowsFrom(quotation.farebreakup)
    const { included: includedItems, notIncluded: notIncludedItems } = inclusionListsFrom(quotation.inclusion)
    const paymentTermRows = paymentTermRowsFrom(quotation.terms)
    const cancellationRows = cancellationRowsFrom(quotation.terms)

    const subtotal = formatPrice(quotation.subtotal, quotation.currency)
    const gstAmount = formatPrice(quotation.gst_amount, quotation.currency)
    const gstPercentage = quotation.gst_percentage
    const totalAmount = formatPrice(quotation.total_amount, quotation.currency) || '—'

    const sendAction = (action, reason = '') => {
        if (!id || actionLoading) return Promise.reject(new Error('missing id'))
        setActionLoading(true)
        return service
            .post(endpoints.venues.proposalAction.replace('{proposal_id}', id), {
                action,
                reason,
                quotation_id: quotation?.quotation_id,
            })
            .finally(() => setActionLoading(false))
    }

    const handleReject = () => {
        sendAction('reject', 'Declined')
            .then(() => {
                setRejectOpen(false)
                // Stay on this page and reflect the new status instead of
                // navigating away — the Reject/Accept/Negotiate buttons
                // below key off quotation.status, so this alone hides them.
                setQuotation((prev) => (prev ? { ...prev, status: 'rejected', status_label: 'Rejected' } : prev))
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
    }

    // 2026-09-21 per Jatin: Accept doesn't call `action` for now (that
    // wiring is coming later, per-page, on his instruction) — it calls
    // confirm-booking-details instead, same GET already used to load
    // venueConfirmBooking.jsx itself.
    const handleAccept = () => {
        if (!id || actionLoading) return
        setActionLoading(true)
        service
            .get(endpoints.venues.proposalConfirmBookingDetails.replace('{proposal_id}', id), {}, { silent: true })
            .then(() => {
                setAcceptOpen(false)
                setQuotation((prev) => (prev ? { ...prev, status: 'accepted', status_label: 'Accepted' } : prev))
                navigate(`/venue-confirm-booking/${id}`)
            })
            .catch(() => {
                // silent:true suppresses the interceptor's own error toast —
                // show one manually since this is a real user-triggered action.
                showToast('Something went wrong. Please try again.', 'error')
            })
            .finally(() => setActionLoading(false))
    }

    const handleNegotiate = () => {
        // The negotiation chat itself is keyed by `chat_room_id` (confirmed
        // 2026-09-21), a different id from the quotation/RFP id (`id`) this
        // page's own actions still use — pass it via nav state.
        const chatRoomId = quotation?.chat_room_id ?? null
        sendAction('negotiate')
            .then(() => navigate(`/venue-negotiate/${id}`, { state: { chatRoomId } }))
            .catch(() => {
                // Even if marking status as "negotiating" fails, still let the
                // user open the chat — the action call is best-effort here.
                navigate(`/venue-negotiate/${id}`, { state: { chatRoomId } })
            })
    }

    return (
        <>
            <section className='commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='venueProposalCard bgWhite commonBorder borderRadius20'>
                                <div className='venueProposalCard__body'>
                                    <div className='venueProposalCard__head'>
                                        <div>
                                            <div className='hotelCard__name'>{proposal.venueName}</div>
                                            {proposal.requestId ? <div className='hotelCard__locationNote'>Request ID: {proposal.requestId}</div> : null}
                                        </div>
                                        {proposal.statusLabel ? (
                                            <span className={`tripStatusBadge ${badgeClass}`}>{proposal.statusLabel}</span>
                                        ) : null}
                                    </div>
                                    <div className='hotelCard__locationNote mt-2'>
                                        {proposal.eventType}
                                        {proposal.preferredDate ? <>&bull; {proposal.preferredDate}</> : null}
                                        {proposal.timeSlot ? <>&bull; {proposal.timeSlot}</> : null}
                                    </div>
                                    {proposal.budgetRange ? <div className='hotelCard__locationNote'>Budget: {proposal.budgetRange}</div> : null}
                                </div>
                            </div>
                        </div>

                        <div className='col-md-12'>
                            <div className='bsSectionTitle'>Venue Information</div>
                            <div className='bsBox'>
                                <div className='hotelCard__name'>{proposal.venueName}</div>
                                <div className='hotelCard__location mt-2'>
                                    <img src={Location} alt="" />
                                    <div className='hotelCard__locationName'>{proposal.location}</div>
                                </div>

                                <div className='venueInfoRow mt-3'>
                                    <div className='bsFareRow'>
                                        <span>Guests:</span>
                                        <span>{proposal.guests}</span>
                                    </div>
                                    {proposal.area ? (
                                        <div className='bsFareRow'>
                                            <span>Area:</span>
                                            <span>{proposal.area}</span>
                                        </div>
                                    ) : null}
                                    {proposal.pricingType ? (
                                        <div className='bsFareRow'>
                                            <span>Pricing Type:</span>
                                            <span>{proposal.pricingType}</span>
                                        </div>
                                    ) : null}
                                    {proposal.estimatedPrice ? (
                                        <div className='bsFareRow'>
                                            <span>Your Estimate:</span>
                                            <span>{proposal.estimatedPrice}</span>
                                        </div>
                                    ) : null}
                                    {proposal.negotiationStatus ? (
                                        <div className='bsFareRow'>
                                            <span>Negotiation Status:</span>
                                            <span>{proposal.negotiationStatus}</span>
                                        </div>
                                    ) : null}
                                </div>

                                {proposal.additionalRequirements ? (
                                    <div className='venueInfoRow mt-3'>
                                        <div className='hotelCard__locationNote'>{proposal.additionalRequirements}</div>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div className='col-md-12'>
                            <div className='commonBookingTab commonBookingTab--split'>
                                {TABS.map((tab) => (
                                    <div
                                        key={tab}
                                        className={`bookTab ${activeTab === tab ? 'active' : ''}`}
                                        onClick={() => setActiveTab(tab)}
                                    >
                                        {tab}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {activeTab === 'Pricing' ? (
                            <div className='col-md-12'>
                                <div className='bsSectionTitle'>Fare Breakup</div>
                                <div className='bsBox'>
                                    {fareBreakupRows.map((item) => (
                                        <div className='bsFareRow' key={item.id}>
                                            <span>{item.label}</span>
                                            <span>{item.value}</span>
                                        </div>
                                    ))}
                                    <div className='bsFareDivider' />
                                    {subtotal ? (
                                        <div className='bsFareRow'>
                                            <span>Subtotal</span>
                                            <span>{subtotal}</span>
                                        </div>
                                    ) : null}
                                    {gstAmount ? (
                                        <div className='bsFareRow'>
                                            <span>GST{gstPercentage ? ` (${gstPercentage}%)` : ''}</span>
                                            <span>{gstAmount}</span>
                                        </div>
                                    ) : null}
                                    <div className='bsFareDivider' />
                                    <div className='bsFareRow bsFareRow--total'>
                                        <span>TOTAL AMOUNT</span>
                                        <span>{totalAmount}</span>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {activeTab === 'Inclusions' ? (
                            <>
                                <div className='col-md-12'>
                                    <div className='bsSectionTitle'>What&apos;s Included</div>
                                    <div className='bsBox'>
                                        {includedItems.length ? (
                                            <ul className='venueChecklist'>
                                                {includedItems.map((item, idx) => (
                                                    <li key={idx}><img src={GreenTick} alt="" />{item}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className='hotelResultsEmpty'>No inclusion details provided.</p>
                                        )}
                                    </div>
                                </div>
                                {notIncludedItems.length ? (
                                    <div className='col-md-12'>
                                        <div className='bsSectionTitle'>Not Included</div>
                                        <div className='bsBox'>
                                            <ul className='venueChecklist venueChecklist--negative'>
                                                {notIncludedItems.map((item, idx) => (
                                                    <li key={idx}><img src={Cross} alt="" />{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                ) : null}
                            </>
                        ) : null}

                        {activeTab === 'Terms' ? (
                            <>
                                <div className='col-md-12'>
                                    <div className='bsSectionTitle'>Payment Terms</div>
                                    <div className='bsBox'>
                                        {paymentTermRows.length ? (
                                            paymentTermRows.map((row) => (
                                                <div className='bsFareRow' key={row.key}>
                                                    <span>{row.label}</span>
                                                    <span>{row.value}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <p className='hotelResultsEmpty'>No payment terms provided.</p>
                                        )}
                                    </div>
                                </div>
                                <div className='col-md-12'>
                                    <div className='bsSectionTitle'>Cancellation Policy</div>
                                    <div className='bsBox'>
                                        {cancellationRows.length ? (
                                            <ul className='bsInfoList'>
                                                {cancellationRows.map((row) => (
                                                    <li key={row.key}>{row.text}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className='hotelResultsEmpty'>No cancellation policy provided.</p>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : null}

                        {proposal.status === 'accepted' ? (
                            <div className='col-md-12'>
                                <p className='venueProposalDetail__statusNote'>You have accepted this proposal.</p>
                            </div>
                        ) : proposal.status === 'rejected' || proposal.status === 'declined' ? (
                            <div className='col-md-12'>
                                <p className='venueProposalDetail__statusNote'>You have rejected this proposal.</p>
                            </div>
                        ) : (
                            <>
                                <div className='col-md-12'>
                                    <button type='button' className='commonBtn w-100' disabled={actionLoading} onClick={() => setAcceptOpen(true)}>Accept Proposal</button>
                                </div>
                                <div className='col-md-12'>
                                    <div className='venueProposalDetail__actions'>
                                        <button type='button' className='venueModalBtn venueModalBtn--danger' disabled={actionLoading} onClick={() => setRejectOpen(true)}>Reject</button>
                                        <button type='button' className='venueModalBtn' disabled={actionLoading} onClick={handleNegotiate}>Negotiate</button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            <Modal open={rejectOpen} onClose={() => setRejectOpen(false)}>
                <div className='venueModal'>
                    <div className='venueModal__icon venueModal__icon--danger'>
                        <img src={Cross} alt="" width={24} height={24} />
                    </div>
                    <div className='venueModal__title'>Reject This Proposal?</div>
                    <p className='venueModal__subtitle'>This action cannot be undone. You can still view other proposals.</p>
                    <div className='venueModal__actions'>
                        <button type='button' className='venueModalBtn' onClick={() => setRejectOpen(false)}>Cancel</button>
                        <button type='button' className='commonBtn' disabled={actionLoading} style={{ background: '#E23744' }} onClick={handleReject}>
                            {actionLoading ? 'Rejecting...' : 'Reject'}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal open={acceptOpen} onClose={() => setAcceptOpen(false)}>
                <div className='venueModal'>
                    <div className='venueModal__icon venueModal__icon--success'>
                        <img src={Success} alt="" width={26} height={26} />
                    </div>
                    <div className='venueModal__title'>Accept This Proposal?</div>
                    <p className='venueModal__subtitle'>You&apos;ll proceed to confirm booking details and make the advance payment.</p>
                    <div className='venueModal__actions'>
                        <button type='button' className='venueModalBtn' onClick={() => setAcceptOpen(false)}>Cancel</button>
                        <button type='button' className='commonBtn' disabled={actionLoading} onClick={handleAccept}>
                            {actionLoading ? 'Confirming...' : 'Confirm'}
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    )
}

export default VenueProposalDetail
